import React from 'react';

import logRocketPromise, { LogRocketInstance } from '@/services/logRocket';

const RELOAD_TIMESTAMP_KEY = 'chunk-reload-at';
const RELOAD_WINDOW_MS = 10000;

const CHUNK_LOAD_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|ChunkLoadError/i;

const isChunkLoadError = (error: Error): boolean =>
  CHUNK_LOAD_ERROR.test(error.message) || CHUNK_LOAD_ERROR.test(error.name);

interface State {
  readonly error?: Error;
}

export class ChunkLoadErrorBoundary extends React.Component<React.PropsWithChildren, State> {
  constructor(props: React.PropsWithChildren) {
    super(props);
    this.state = { error: undefined };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error: error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    if (!isChunkLoadError(error)) {
      return;
    }
    console.error('ChunkLoadErrorBoundary caught a chunk-load error:', error, errorInfo);
    void logRocketPromise.then((logRocket: LogRocketInstance): void => {
      logRocket.captureException(error, {
        extra: {
          componentStack: errorInfo.componentStack ?? '',
          source: 'chunk_load_error_boundary',
        },
      });
    });
  }

  render(): React.ReactNode {
    const { children } = this.props;
    const { error } = this.state;

    if (error) {
      if (isChunkLoadError(error)) {
        const lastReloadAt = Number(sessionStorage.getItem(RELOAD_TIMESTAMP_KEY) ?? '0');
        if (Date.now() - lastReloadAt > RELOAD_WINDOW_MS) {
          sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(Date.now()));
          window.location.reload();
          return null;
        }
      }
      throw error;
    }

    return children;
  }
}
